import { useTranslations } from 'next-intl'
import { useEffect, useRef, type ChangeEvent } from 'react'
import toast from 'react-hot-toast'
import IconLocateMe from 'asset/icon/general/locateme_s.svg'
import IconSearch from 'asset/icon/search.svg'
import LocationPickerItem from 'component/block/locationPicker/item'
import LocationListContainer from 'component/block/locationPicker/listContainer'
import LocationGridItem from 'component/block/locationPicker/gridItem'
import SuggestedListItem from 'component/block/suggestedListItem'
import TextAreaAutoResize from 'component/block/textAreaAutoResize'
import useDebounceInput from 'hook/useDebounceInput'
import useGeoLocation from 'hook/useGeoLocation'
import { useLocationPicker } from 'hook/useLocationPicker'
import useScrollDetector from 'hook/useScrollDetector'
import { scrollIndexStore } from 'store/global/feed-scroll-index'
import { useSpotLocationPickerStore } from 'store/global/location-picker'
import { usePickerOpenStore } from 'store/global/picker-open'
import { playStateStore } from 'store/global/video'
import { useSpotRecentLocationsStore as useRecentLocations } from 'store/recent-search'
import { useIsMobile } from 'store/server-context/device'
import type { ISpotLocationPickerV2 } from 'type/feed'
import getGeoLocation from 'util/geGeoLocation'
import { normalizeAsset } from 'util/normalizeAsset'

const LocationDropdown = () => {
  const t = useTranslations()
  const { isScrolling } = useScrollDetector()
  const { input, debounceInput, updateInput } = useDebounceInput()
  const isMobile = useIsMobile()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const setShowCard = usePickerOpenStore(state => state.setPickerOpen)

  const recentLocations = useRecentLocations(state => state.recentLocations)
  const addRecentLocation = useRecentLocations(state => state.addRecentLocation)
  const removeRecentLocation = useRecentLocations(
    state => state.removeRecentLocation
  )
  const currentGeo = useSpotLocationPickerStore(state => state.currentGeo)
  const setPickedLocation = useSpotLocationPickerStore(
    state => state.setPickedLocation
  )
  const clearPickedLocation = useSpotLocationPickerStore(
    state => state.clearPickedLocation
  )

  const geoLocation = useGeoLocation()
  const { yourLocation, updateGps, permission } = geoLocation

  const searchKeyword = debounceInput.trim()
  const hasSearchKeyword = !!searchKeyword

  const { showLocations, showLoading, noSearchResult, error } =
    useLocationPicker<ISpotLocationPickerV2>(
      'spot',
      searchKeyword,
      geoLocation,
      currentGeo
    )

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target
    updateInput(value)
  }

  const handleClickItem = (item: ISpotLocationPickerV2) => {
    toast.remove()
    toast(
      t('feed.change_place_level_success', { location: item.display_name }),
      {
        duration: 3000
      }
    )
    scrollIndexStore.setIndex(0)
    addRecentLocation(item)
    setPickedLocation(item)
    setShowCard(false)
    playStateStore.play()
  }

  const handleClickYourLocation = () => {
    if (!yourLocation) return
    toast.remove()
    toast(
      t('feed.change_place_level_success', {
        location: yourLocation.display_name
      }),
      {
        duration: 3000
      }
    )
    clearPickedLocation()
    setShowCard(false)
    playStateStore.play()
  }

  const handleEnableLocation = async (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    event.preventDefault()
    event.stopPropagation()
    try {
      const location = await getGeoLocation()
      updateGps(location)
    } catch (error) {
      toast(t('common.no_location_permission'), {
        duration: 3000
      })
    }
  }

  const handleClear = () => {
    updateInput('')
    textAreaRef.current?.focus()
  }

  const onScroll = () => {
    textAreaRef.current?.blur()
  }

  const theme = isMobile ? 'still' : 'light'

  useEffect(() => {
    if (isScrolling) {
      onScroll()
    }
  }, [isScrolling])

  return (
    <div className='h-full max-h-[672px] playsee-mb:max-h-full'>
      <div className='p-[12px]'>
        <TextAreaAutoResize
          ref={textAreaRef}
          value={input}
          showClear={true}
          onClear={handleClear}
          onChange={handleChange}
          placeholder={t('search.search')!}
          maxRows={1}
          prefix={
            <IconSearch width={18} height={18} className='fill-label-l1' />
          }
        />
      </div>

      <LocationListContainer
        onScroll={onScroll}
        showLoading={showLoading}
        showNoResult={Boolean(
          !showLoading && hasSearchKeyword && noSearchResult
        )}
        keyword={searchKeyword}
      >
        {!showLoading && (
          <>
            {/* default show 'explore on map', 'your location', 'recent locations' */}
            {!hasSearchKeyword && (
              <>
                {permission && yourLocation ? (
                  <LocationPickerItem onClick={handleClickYourLocation}>
                    <SuggestedListItem
                      theme={theme}
                      icon={<IconLocateMe className='fill-current sq-[24px]' />}
                      title={yourLocation.display_name}
                      subTitle={t('feed.your_location')}
                    />
                  </LocationPickerItem>
                ) : (
                  <LocationPickerItem onClick={handleEnableLocation}>
                    <SuggestedListItem
                      theme={theme}
                      icon={<IconLocateMe className='fill-current sq-[24px]' />}
                      title={t('common.enable_location_access')}
                      subTitle={t('feed.your_location')}
                    />
                  </LocationPickerItem>
                )}
                <p className='px-4 text-header'>{t('search.recent')}</p>
                <div className='grid grid-cols-3 gap-[1px]'>
                  {recentLocations.map((location, index) => (
                    <LocationGridItem
                      key={'recent' + location.display_name}
                      title={location.display_name}
                      src={normalizeAsset(location.asset).src}
                      onClick={() => handleClickItem(location)}
                      onClose={event => {
                        event.stopPropagation()
                        removeRecentLocation(index)
                      }}
                    />
                  ))}
                </div>
              </>
            )}

            {!error && (
              <div className='grid grid-cols-3 gap-[1px]'>
                {showLocations.map((location, index) =>
                  location.level_type !== 'spot' ? (
                    <div
                      key={'search' + index}
                      className='cursor-default px-[16px] pb-[4px] pt-[12px] text-lg font-bold text-label-l1 playsee-mb:text-label_still-l1 col-span-3'
                    >
                      {location.display_name}
                    </div>
                  ) : (
                    <LocationGridItem
                      key={location.display_name}
                      title={location.display_name}
                      src={normalizeAsset(location.asset).src}
                      onClick={() => handleClickItem(location)}
                    />
                  )
                )}
              </div>
            )}
          </>
        )}
      </LocationListContainer>
    </div>
  )
}

export default LocationDropdown
